import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Calendar, Clock, User, MapPin, Bell, Star, 
  ChevronRight, Activity, Search, Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';

export default function PatientDashboard() {
  const { api, profile } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [appointmentsRes, notificationsRes] = await Promise.all([
        api.get('/patient/appointments?status=pending'),
        api.get('/patient/notifications')
      ]);
      setAppointments(appointmentsRes.data.slice(0, 3));
      setNotifications(notificationsRes.data.filter(n => !n.is_read).slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch patient dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', className: 'bg-amber-100 text-amber-700' },
      confirmed: { label: 'Confirmada', className: 'bg-emerald-100 text-emerald-700' },
      completed: { label: 'Realizada', className: 'bg-slate-100 text-slate-700' },
      cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-700' },
      no_show: { label: 'Não compareceu', className: 'bg-red-100 text-red-700' },
    };
    const { label, className } = statusMap[status] || statusMap.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-dashboard">
      <Navbar />

      <div className="container-app py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Olá, {profile?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600">Gerencie suas consultas e cuide da sua saúde</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Link to="/buscar" className="bg-white rounded-2xl border border-slate-100 p-6 hover:border-primary transition-colors group">
            <Search className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">Buscar Médico</h3>
            <p className="text-sm text-slate-500">Encontrar especialista</p>
          </Link>
          <Link to="/paciente/consultas" className="bg-white rounded-2xl border border-slate-100 p-6 hover:border-primary transition-colors group">
            <Calendar className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">Consultas</h3>
            <p className="text-sm text-slate-500">Ver agendamentos</p>
          </Link>
          <Link to="/paciente/lista-espera" className="bg-white rounded-2xl border border-slate-100 p-6 hover:border-primary transition-colors group">
            <Clock className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">Lista de Espera</h3>
            <p className="text-sm text-slate-500">Gerenciar filas</p>
          </Link>
          <Link to="/paciente/perfil" className="bg-white rounded-2xl border border-slate-100 p-6 hover:border-primary transition-colors group">
            <User className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">Meu Perfil</h3>
            <p className="text-sm text-slate-500">Dados e convênio</p>
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Upcoming Appointments */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Próximas Consultas</h2>
                <Link to="/paciente/consultas" className="text-primary text-sm font-medium hover:underline flex items-center gap-1">
                  Ver todas <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-24 bg-slate-100 rounded-xl"></div>
                    </div>
                  ))}
                </div>
              ) : appointments.length > 0 ? (
                <div className="space-y-4">
                  {appointments.map((appointment) => (
                    <div key={appointment.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center border border-slate-100">
                        <User className="w-7 h-7 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(appointment.status)}
                        </div>
                        <h3 className="font-medium text-slate-900 truncate">{appointment.doctor_name}</h3>
                        <p className="text-sm text-slate-600">{appointment.specialty_name}</p>
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(appointment.date), 'dd/MM/yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {appointment.time_slot}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">Nenhuma consulta agendada</p>
                  <Link to="/buscar">
                    <Button size="sm" className="bg-primary">Agendar Consulta</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Notificações
              </h2>
              {notifications.length > 0 && (
                <Badge className="bg-primary text-white">{notifications.length}</Badge>
              )}
            </div>

            {notifications.length > 0 ? (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div key={notification.id} className="p-3 bg-slate-50 rounded-xl">
                    <p className="font-medium text-slate-900 text-sm">{notification.title}</p>
                    <p className="text-xs text-slate-600 mt-1">{notification.message}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {format(new Date(notification.created_at), "dd/MM 'às' HH:mm")}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Sem notificações</p>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 bg-primary rounded-2xl p-6 md:p-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold mb-2">Precisa de uma consulta urgente?</h3>
              <p className="text-emerald-100">Encontre médicos com horários disponíveis para hoje</p>
            </div>
            <Link to="/buscar?available_today=true">
              <Button className="bg-white text-primary hover:bg-slate-100">
                <Clock className="w-4 h-4 mr-2" />
                Ver Horários Hoje
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
